import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ CRW-02 at v1.9106, crew part 4D-2: unavailability and leave. Entered by the office for
// anyone, decided with leave.approve; a shift saved over either answers time-off-clash, and is sent
// again with throughTimeOff when the office books anyway.

export type TimeOffEntry = components['schemas']['RosterTimeOffView'];
export type Unavailability = components['schemas']['UnavailabilityView'];
export type Leave = components['schemas']['LeaveView'];
export type LeaveType = components['schemas']['LeaveTypeView'];
export type Decided = components['schemas']['DecidedView'];
export type When =
  { days: { from: string; to: string } } | { times: { start: string; end: string } };
export type UnavailabilityReason = 'other_work' | 'training' | 'personal' | 'other';

export const REASONS: Record<UnavailabilityReason, string> = {
  other_work: 'Other work',
  training: 'Training',
  personal: 'Personal',
  other: 'Other',
};

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title, error as Record<string, unknown>)
    : new ApiProblem('unknown', fallback);
}

/** A save landed on someone's unavailability or leave: say so, and offer to book anyway. */
export const isTimeOffClash = (error: unknown): error is ApiProblem =>
  error instanceof ApiProblem && error.type.endsWith('time-off-clash');

/** A refusal from booking or offering to several that was someone's time off (CB-CONV §12 4D-1c). */
export const isTimeOffReason = (reason: string) =>
  reason.startsWith('Unavailable (') || reason.startsWith('On leave (');

export const leaveTypesQuery = queryOptions({
  queryKey: ['leave-types'],
  queryFn: async (): Promise<LeaveType[]> => {
    const { data, error } = await api.GET('/v1/leave-types', { params: { query: {} } });
    if (error) throw problem(error, 'Could not load the leave types.');
    return data;
  },
});

export async function addUnavailability(
  crewMemberId: string,
  input: {
    reason: UnavailabilityReason;
    when: When;
    note?: string | null;
    repeatsWeekly?: boolean;
  },
) {
  const { data, error } = await api.POST('/v1/crew/{id}/unavailability', {
    params: { path: { id: crewMemberId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not save that.');
  return data;
}

export async function retireUnavailability(id: string) {
  const { error } = await api.POST('/v1/unavailability/{id}/retire', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not remove that.');
}

export async function recordLeave(
  crewMemberId: string,
  input: { leaveTypeId: string; when: When; note?: string | null },
) {
  const { data, error } = await api.POST('/v1/crew/{id}/leave', {
    params: { path: { id: crewMemberId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not save that leave.');
  return data;
}

export async function decideLeave(id: string, state: 'approve' | 'decline', note?: string | null) {
  const path = state === 'approve' ? '/v1/leave/{id}/approve' : '/v1/leave/{id}/decline';
  const { data, error } = await api.POST(path, {
    params: { path: { id } },
    body: { note: note ?? null },
  });
  if (error) throw problem(error, 'Could not decide that leave.');
  return data;
}

export async function cancelLeave(id: string) {
  const { data, error } = await api.POST('/v1/leave/{id}/cancel', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not cancel that leave.');
  return data;
}

export type ClassSetting = components['schemas']['ClassSettingView'];
export type CrewClassKey = 'employee' | 'contractor' | 'subcontractor';

export const unavailabilityQuery = (crewMemberId: string) =>
  queryOptions({
    queryKey: ['crew', crewMemberId, 'unavailability'],
    queryFn: async (): Promise<Unavailability[]> => {
      const { data, error } = await api.GET('/v1/crew/{id}/unavailability', {
        params: { path: { id: crewMemberId } },
      });
      if (error) throw problem(error, 'Could not load their unavailability.');
      return data;
    },
  });

export const leaveQuery = (crewMemberId: string) =>
  queryOptions({
    queryKey: ['crew', crewMemberId, 'leave'],
    queryFn: async (): Promise<Leave[]> => {
      const { data, error } = await api.GET('/v1/crew/{id}/leave', {
        params: { path: { id: crewMemberId } },
      });
      if (error) throw problem(error, 'Could not load their leave.');
      return data;
    },
  });

/** For Home: leave asked for and not yet decided. */
export const pendingLeaveQuery = queryOptions({
  queryKey: ['leave', 'pending'],
  queryFn: async (): Promise<Leave[]> => {
    const { data, error } = await api.GET('/v1/leave/pending');
    if (error) throw problem(error, 'Could not load the leave to approve.');
    return data;
  },
});

export const timeOffSettingsQuery = queryOptions({
  queryKey: ['settings', 'crew-time-off'],
  queryFn: async (): Promise<ClassSetting[]> => {
    const { data, error } = await api.GET('/v1/settings/crew-time-off');
    if (error) throw problem(error, 'Could not load who may enter time off.');
    return data;
  },
});

export async function setTimeOffSettings(settings: ClassSetting[]) {
  const { data, error } = await api.PUT('/v1/settings/crew-time-off', { body: { settings } });
  if (error) throw problem(error, 'Could not save that.');
  return data;
}

/** A person's own override; null follows their class. */
export async function setTimeOffOverride(
  crewMemberId: string,
  override: { mayMarkUnavailable?: boolean | null; mayRequestLeave?: boolean | null },
) {
  const { error } = await api.PUT('/v1/crew/{id}/time-off-override', {
    params: { path: { id: crewMemberId } },
    body: override,
  });
  if (error) throw problem(error, 'Could not save that.');
}

export const allLeaveTypesQuery = queryOptions({
  queryKey: ['leave-types', 'all'],
  queryFn: async (): Promise<LeaveType[]> => {
    const { data, error } = await api.GET('/v1/leave-types', {
      params: { query: { includeRetired: true } },
    });
    if (error) throw problem(error, 'Could not load the leave types.');
    return data;
  },
});

export async function addLeaveType(input: { name: string; paid: boolean; needsApproval: boolean }) {
  const { data, error } = await api.POST('/v1/leave-types', { body: input });
  if (error) throw problem(error, 'Could not add that leave type.');
  return data;
}

export async function updateLeaveType(
  id: string,
  changes: { name?: string; paid?: boolean; needsApproval?: boolean; retired?: boolean },
) {
  const { data, error } = await api.PATCH('/v1/leave-types/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not change that leave type.');
  return data;
}

/** "2 – 4 Mar", "9 Mar 09:00 – 14:00": as entered, in its own zone. */
export function whenLabel(when: {
  days?: { from: string; to: string };
  times?: { start: string; end: string };
}): string {
  const day = (d: string) =>
    new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
      new Date(`${d.slice(0, 10)}T00:00:00Z`),
    );
  if (when.days) {
    return when.days.from === when.days.to
      ? day(when.days.from)
      : `${day(when.days.from)} – ${day(when.days.to)}`;
  }
  const t = when.times!;
  const sameDay = t.start.slice(0, 10) === t.end.slice(0, 10);
  return `${day(t.start)} ${t.start.slice(11, 16)} – ${sameDay ? '' : `${day(t.end)} `}${t.end.slice(11, 16)}`;
}

// — The crew's own, on My shifts (crew part 4D-3) —

export type Mine<T> = { may: boolean; items: T[] };

export const myUnavailabilityQuery = queryOptions({
  queryKey: ['my', 'unavailability'],
  queryFn: async (): Promise<Mine<Unavailability>> => {
    const { data, error } = await api.GET('/v1/my/unavailability');
    if (error) throw problem(error, 'Could not load your unavailability.');
    return data;
  },
});

export const myLeaveQuery = queryOptions({
  queryKey: ['my', 'leave'],
  queryFn: async (): Promise<Mine<Leave>> => {
    const { data, error } = await api.GET('/v1/my/leave');
    if (error) throw problem(error, 'Could not load your leave.');
    return data;
  },
});

export const myLeaveTypesQuery = queryOptions({
  queryKey: ['my', 'leave-types'],
  queryFn: async (): Promise<LeaveType[]> => {
    const { data, error } = await api.GET('/v1/my/leave-types');
    if (error) throw problem(error, 'Could not load the leave types.');
    return data;
  },
});

export async function addMyUnavailability(input: {
  reason: UnavailabilityReason;
  when: When;
  note?: string | null;
  repeatsWeekly?: boolean;
}) {
  const { data, error } = await api.POST('/v1/my/unavailability', { body: input });
  if (error) throw problem(error, 'Could not save that.');
  return data;
}

export async function retireMyUnavailability(id: string) {
  const { error } = await api.POST('/v1/my/unavailability/{id}/retire', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not remove that.');
}

export async function askMyLeave(input: { leaveTypeId: string; when: When; note?: string | null }) {
  const { data, error } = await api.POST('/v1/my/leave', { body: input });
  if (error) throw problem(error, 'Could not ask for that leave.');
  return data;
}

export async function cancelMyLeave(id: string) {
  const { data, error } = await api.POST('/v1/my/leave/{id}/cancel', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not cancel that leave.');
  return data;
}
